import React from 'react'
import { Platform, PlatformEnvironment, PlatformGrant } from '../../../../../Models/AdminManagement'
import {
  environmentLabel,
  hasGrant,
  platformColor,
  platformDescription,
  platformLabel,
  toggleGrant,
} from '../../../../../Services/adminManagementService'
import './PlatformGrantMatrix.scss'

interface PlatformGrantMatrixProps {
  platforms: Platform[]
  environments: PlatformEnvironment[]
  value: PlatformGrant[]
  onChange: (grants: PlatformGrant[]) => void
  disabled?: boolean
}

// Access is granted as (platform, environment) pairs
const PlatformGrantMatrix: React.FC<PlatformGrantMatrixProps> = ({
  platforms,
  environments,
  value,
  onChange,
  disabled = false,
}) => {
  const toggleRow = (platform: Platform) => {
    const rowComplete = environments.every((env) => hasGrant(value, platform, env))

    onChange(
      rowComplete
        ? value.filter((g) => g.platform !== platform)
        : [
          ...value.filter((g) => g.platform !== platform),
          ...environments.map((environment) => ({ platform, environment })),
        ],
    )
  }

  if (platforms.length === 0) {
    return <div className='text-muted small'>No platforms available.</div>
  }

  return (
    <div className='PlatformGrantMatrix table-responsive'>
      <table className='table align-middle mb-0'>
        <thead>
          <tr>
            <td>Platform</td>
            {environments.map((env) => (
              <td key={env} className='text-center'>
                {environmentLabel(env)}
              </td>
            ))}
            <td className='text-center'>All</td>
          </tr>
        </thead>
        <tbody>
          {platforms.map((platform) => {
            const rowComplete = environments.every((env) => hasGrant(value, platform, env))

            return (
              <tr key={platform}>
                <td>
                  <div className='platform-title'>
                    <span
                      className='platform-dot'
                      style={{ background: platformColor(platform) }}
                    />
                    {platformLabel(platform)}
                  </div>
                  {platformDescription(platform) && (
                    <div className='platform-desc'>{platformDescription(platform)}</div>
                  )}
                </td>

                {environments.map((environment) => (
                  <td key={environment} className='text-center'>
                    <input
                      type='checkbox'
                      className='form-check-input'
                      aria-label={`${platformLabel(platform)} on ${environmentLabel(environment)}`}
                      checked={hasGrant(value, platform, environment)}
                      disabled={disabled}
                      onChange={() => onChange(toggleGrant(value, platform, environment))}
                    />
                  </td>
                ))}

                <td className='text-center'>
                  <button
                    type='button'
                    className='btn btn-sm btn-row-toggle'
                    disabled={disabled}
                    onClick={() => toggleRow(platform)}
                  >
                    {rowComplete ? 'Clear' : 'All'}
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default PlatformGrantMatrix
